import { api, clearAllClientState, updateSessionCache } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { useAppDispatch } from '@/store/hooks';
import type { Organizer } from '@eventflow/shared-types';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { type UserRole, setLoading, setUser } from './authSlice';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Verificar sesión inicial
    const checkSession = async () => {
      try {
        dispatch(setLoading(true));

        const {
          data: { session },
        } = await supabase.auth.getSession();

        // Actualizar caché de sesión
        updateSessionCache(session);

        if (session?.user) {
          try {
            // Verificar roles primero
            const rolesResp = await api.get<{ roles: Array<{ role_name: string }> }>(
              `/users/${session.user.id}/roles`
            );
            const roleNames = rolesResp.roles.map((r) => r.role_name) as UserRole[];
            const isOrganizer = roleNames.includes('organizer');
            const isSuperadmin = roleNames.includes('superadmin');

            // Superadmin no necesita perfil de organizador (tiene acceso total)
            // Solo intentar obtener perfil si es organizer Y NO es superadmin
            if (isOrganizer && !isSuperadmin) {
              try {
                const organizer = await api.get<Organizer>('/auth/profile');
                dispatch(setUser({ user: session.user, organizer, roles: roleNames }));
              } catch {
                dispatch(setUser({ user: session.user, organizer: null, roles: roleNames }));
              }
            } else {
              // Superadmin o cliente - no necesita perfil de organizador
              dispatch(setUser({ user: session.user, organizer: null, roles: roleNames }));
            }
          } catch {
            dispatch(setUser({ user: session.user, organizer: null, roles: ['client'] }));
          }
        } else {
          dispatch(setUser({ user: null, organizer: null, roles: [] }));
        }
      } catch {
        dispatch(setUser({ user: null, organizer: null }));
      }
    };

    checkSession();

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Actualizar caché de sesión siempre que cambie el estado
      updateSessionCache(session);

      if (event === 'SIGNED_IN' && session?.user) {
        try {
          // Verificar roles
          const rolesResp = await api.get<{ roles: Array<{ role_name: string }> }>(
            `/users/${session.user.id}/roles`
          );
          const roleNames = rolesResp.roles.map((r) => r.role_name) as UserRole[];
          const isSuperadmin = roleNames.includes('superadmin');
          const isOrganizer = roleNames.includes('organizer');

          // Superadmin no necesita perfil (tiene acceso total)
          // Solo obtener perfil si es organizer Y NO es superadmin
          if (isOrganizer && !isSuperadmin) {
            try {
              const organizer = await api.get<Organizer>('/auth/profile');
              dispatch(setUser({ user: session.user, organizer, roles: roleNames }));
            } catch {
              dispatch(setUser({ user: session.user, organizer: null, roles: roleNames }));
            }
          } else {
            // Superadmin o cliente
            dispatch(setUser({ user: session.user, organizer: null, roles: roleNames }));
          }
        } catch {
          dispatch(setUser({ user: session.user, organizer: null, roles: ['client'] }));
        }
      } else if (event === 'SIGNED_OUT') {
        clearAllClientState();
        dispatch(setUser({ user: null, organizer: null, roles: [] }));
      } else if (event === 'TOKEN_REFRESHED' && session) {
        const currentUser = session.user;
        if (currentUser) {
          try {
            const rolesResp = await api.get<{ roles: Array<{ role_name: string }> }>(
              `/users/${currentUser.id}/roles`
            );
            const roleNames = rolesResp.roles.map((r) => r.role_name) as UserRole[];
            const isSuperadmin = roleNames.includes('superadmin');
            const isOrganizer = roleNames.includes('organizer');

            // Superadmin no necesita perfil, solo organizer puro
            if (isOrganizer && !isSuperadmin) {
              try {
                const organizer = await api.get<Organizer>('/auth/profile');
                dispatch(setUser({ user: currentUser, organizer, roles: roleNames }));
              } catch {
                dispatch(setUser({ user: currentUser, organizer: null, roles: roleNames }));
              }
            } else {
              dispatch(setUser({ user: currentUser, organizer: null, roles: roleNames }));
            }
          } catch {
            dispatch(setUser({ user: currentUser, organizer: null, roles: ['client'] }));
          }
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  return <>{children}</>;
}
